import { getText } from '@storebrand-digital/fms-translations';
import {
  HomeBuyerFacade,
  HomeBuyerProductCategory,
} from 'Api/insurance/types/products/homeBuyer.types';
import {
  ErgoProductName,
  ErgoProducts,
  ProductName,
} from 'Constants/products.constants';
import { ChangeFormValues } from 'Forms/ChangeForm/changeForm.types';
import { ChangeCabinFormValues } from 'Forms/ChangeForm/products/cabin/changeCabinForm.types';
import { ChangeCarFormValues } from 'Forms/ChangeForm/products/car/changeCarForm.types';
import { ChangeBuildingContentsFormValues } from 'Forms/ChangeForm/products/commonBuilding/commonBuilding.types';
import { ChangeHouseFormValues } from 'Forms/ChangeForm/products/house/changeHouseForm.types';
import { ChangeMotorcycleFormValues } from 'Forms/ChangeForm/products/motorcycle/changeMotorcycleForm.types';
import { ChangeTravelFormValues } from 'Forms/ChangeForm/products/travel/changeTravelForm.types';
import { PRODUCT_NAMES_MAPPED_TO_FMS } from '../api/insurance/insurance.constants';

export function getInsuranceProductName(productName: string): string {
  if (productName in PRODUCT_NAMES_MAPPED_TO_FMS) {
    return getText(PRODUCT_NAMES_MAPPED_TO_FMS[productName]);
  }
  if (ErgoProducts.includes(productName as ErgoProductName)) {
    // We don't want to show product name on the overview page.
    return '';
  }
  return productName;
}

// TODO: https://jira.storebrand.no/browse/CLI-413
const insurancesWithOldDetails = [
  'A+ Familieulykke',
  'A+ Gruppeliv',
  'A+ Helseforsikring',
  'A+ Innbo',
  'A+ Kritisk Sykdom',
  'Båt',
  'Innskuddspensjon',
  'Livkonto',
  'Personalforsikring',
  'Spedbarnsforsikring',
  'Fripolise',
  'Fripolise med investeringsvalg, FMI',
  'Barnepensjon fra arbeidsgiver (pensjonskasse)',
  'Barnepensjon fra arbeidsgiver (fripolise)',
  'Barnetillegg til uførepensjon fra arbeidsgiver (pensjonskasse)',
  'Samboerpensjon fra arbeidsgiver (fripolise)',
  'Ektefellepensjon fra arbeidsgiver (fripolise)',
  'Uførepensjon fra arbeidsgiver (fripolise)',
  'Ulykke',
  'Gjeldsforsikring',
];

export function getInsuranceHasOldDetails(productName: string): boolean {
  return insurancesWithOldDetails.includes(productName);
}

export const isContentsProduct = (
  values: ChangeFormValues,
): values is ChangeBuildingContentsFormValues => {
  return 'coverageAmount' in values;
};

export const isHouseForm = (
  values: ChangeFormValues,
): values is ChangeHouseFormValues => {
  return 'rentalStatus' in values;
};
export const isCabinForm = (
  values: ChangeFormValues,
): values is ChangeCabinFormValues => {
  return 'hasRental' in values;
};

export const isCoverageInForm = (
  values: ChangeFormValues,
): values is ChangeCarFormValues | ChangeMotorcycleFormValues => {
  return 'coverage' in values;
};

export const isTravelForm = (
  values: ChangeFormValues,
): values is ChangeTravelFormValues => {
  return 'coverageLength' in values;
};

export const isGarageInForm = (
  values: ChangeFormValues,
): values is ChangeMotorcycleFormValues => {
  return 'garage' in values;
};

export const isMcLockerInForm = (
  values: ChangeFormValues,
): values is ChangeMotorcycleFormValues => {
  return 'mcLocker' in values;
};

export const isUsabilityCodeInForm = (
  values: ChangeFormValues,
): values is ChangeMotorcycleFormValues => {
  return 'usabilityCode' in values;
};

export const isRentalTypeInForm = (
  values: ChangeFormValues,
): values is ChangeCarFormValues => {
  return 'rentalType' in values;
};

export const isUsedByYoungerDriverInForm = (
  values: ChangeFormValues,
): values is ChangeCarFormValues => {
  return 'usedByYoungerDriver' in values;
};

export const isYearlyKmNeedInForm = (
  values: ChangeFormValues,
): values is ChangeCarFormValues => {
  return 'yearlyKmNeed' in values;
};

export const getHomeProductName = (product: ProductName): string => {
  switch (product) {
    case ProductName.HOME_SELLER:
      return 'Boligselgerforsikring';
    case ProductName.HOME_BUYER_HOUSE:
    case ProductName.HOME_BUYER_HOUSE_CONTENTS:
      return 'Boligkjøperforsikring';
    case ProductName.HOME_BUYER_INTEREST:
      return 'Renteforsikring';
    case ProductName.HOME_BUYER_MOVE:
      return 'Flytteforsikring';
    default:
      return '';
  }
};

export const getHomeBuyerProductCategory = (
  homeBuyerFacade?: HomeBuyerFacade,
): HomeBuyerProductCategory =>
  homeBuyerFacade === HomeBuyerFacade.RECREATIONAL_RESIDENCE
    ? HomeBuyerProductCategory.CABIN
    : HomeBuyerProductCategory.HOUSE;
